import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useAdminOrders, useKitchenStations } from '../../lib/admin-hooks';
import type { ActionResult } from '../../lib/admin-hooks';
import { useTenant } from '../../lib/tenant-context';
import { cn } from '../../lib/utils';
import { Clock, CheckCircle2, AlertTriangle, ShoppingBag, Bike, Store, ChefHat, X } from 'lucide-react';
interface Toast {
  id: number;
  type: 'success' | 'error';
  message: string;
}
export function CajaOverviewPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const tenantCtx = useTenant();
  const {
    stations,
    loading: stationsLoading
  } = useKitchenStations(restaurant?.id);
  const {
    orders,
    loading: ordersLoading,
    acceptOrder,
    startOrderPreparation,
    markOrderReady,
    deliverOrder,
    cancelOrder
  } = useAdminOrders(restaurant?.id);
  const [updatingOrders, setUpdatingOrders] = useState<Set<string>>(new Set());
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [activeFilter, setActiveFilter] = useState<'all' | 'new' | 'preparing' | 'ready' | 'delayed'>('all');
  const isCapricho = String(restaurant?.id) === '2' || restaurant?.slug === 'capricho-de-canela';
  const addToast = useCallback((type: 'success' | 'error', message: string) => {
    const id = Date.now();
    setToasts(prev => [...prev, {
      id,
      type,
      message
    }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3500);
  }, []);
  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);
  if (restLoading || !restaurant) {
    return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-1523">
        <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-1524">
          <div className="w-16 h-16 rounded-full bg-[#141414] animate-pulse" data-id="element-1525" />
        </div>
      </AdminLayout>;
  }
  // Filter active orders
  const activeOrders = orders.filter((o: any) => !['delivered', 'cancelled', 'out_for_delivery'].includes(o.status));
  const sortedOrders = [...activeOrders].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  const newOrders = activeOrders.filter(o => o.status === 'new' || o.status === 'accepted');
  const preparingOrders = activeOrders.filter(o => o.status === 'preparing');
  const readyOrders = activeOrders.filter(o => o.status === 'ready' || o.status === 'searching_driver');
  const delayedOrders = activeOrders.filter(o => {
    const waitMinutes = Math.floor((new Date().getTime() - new Date(o.created_at).getTime()) / 60000);
    return waitMinutes >= 15;
  });
  const getShortOrderNumber = (orderNumber: string) => {
    if (!orderNumber) return '';
    const parts = orderNumber.split('-');
    return parts[parts.length - 1] || orderNumber;
  };
  // Action handler — now with feedback
  const handleOrderAction = async (orderId: string, action: string) => {
    setUpdatingOrders(prev => new Set(prev).add(orderId));
    let result: ActionResult = {
      success: false,
      error: 'Acción desconocida'
    };
    try {
      switch (action) {
        case 'send_to_kitchen':
          {
            // Combined: accept + start preparation in one action
            result = await acceptOrder(orderId);
            if (result.success) {
              result = await startOrderPreparation(orderId);
            }
            if (result.success) addToast('success', '🔥 Enviado a cocina');
            break;
          }
        case 'start_preparation':
          result = await startOrderPreparation(orderId);
          if (result.success) addToast('success', '🔥 Enviado a cocina');
          break;
        case 'mark_ready':
          result = await markOrderReady(orderId);
          if (result.success) addToast('success', '✅ Pedido marcado como listo');
          break;
        case 'deliver':
          result = await deliverOrder(orderId);
          if (result.success) addToast('success', '🚀 Pedido entregado');
          break;
        case 'capricho_receive':
          {
            // Combined: accept + start preparation
            result = await acceptOrder(orderId);
            if (result.success) {
              result = await startOrderPreparation(orderId);
            }
            if (result.success) addToast('success', '✅ Pedido recibido');
            break;
          }
        case 'capricho_complete':
          {
            // Combined: mark ready + deliver
            result = await markOrderReady(orderId);
            if (result.success) {
              result = await deliverOrder(orderId);
            }
            if (result.success) addToast('success', '🎉 Pedido completado');
            break;
          }
        case 'cancel':
          if (confirm('¿Estás seguro de cancelar este pedido?')) {
            result = await cancelOrder(orderId);
            if (result.success) addToast('success', '❌ Pedido cancelado');
          } else {
            result = {
              success: true
            };
          }
          break;
      }
      if (!result.success && result.error) {
        addToast('error', result.error);
      }
    } catch (err: any) {
      addToast('error', err?.message || 'Error inesperado');
    } finally {
      setUpdatingOrders(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-1526">
      <div className="min-h-screen bg-[#0B0B0B] p-4 sm:p-6 text-white font-sans" data-id="element-1527">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-1528">
          <div data-id="element-1529">
            <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight flex items-center gap-3" data-id="element-1530">
              Control de Caja
              <span className="bg-white/10 text-white/70 px-3 py-1 rounded-full text-sm font-bold tabular-nums" data-id="element-1531">
                {activeOrders.length} activos
              </span>
            </h1>
            <p className="text-white/50 text-sm mt-1" data-id="element-1532">
              Vista general de todas las estaciones y pedidos en curso
            </p>
          </div>
        </div>

        {/* Stats Row — clickable filters */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8" data-id="element-1533">
          <button onClick={() => setActiveFilter(activeFilter === 'new' ? 'all' : 'new')} className={cn('bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-l-blue-500 shadow-lg text-left cursor-pointer transition-all active:scale-[0.97]', activeFilter === 'new' ? 'border-blue-500 ring-2 ring-blue-500/40 bg-blue-500/10' : 'border-white/5 hover:bg-white/[0.03]')} data-id="element-1534">
            <div className="flex items-center justify-between mb-2" data-id="element-1535">
              <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center" data-id="element-1536">
                <ShoppingBag className="w-4 h-4 text-blue-400" data-id="element-1537" />
              </div>
              <span className="text-2xl font-black tabular-nums" data-id="element-1538">
                {newOrders.length}
              </span>
            </div>
            <p className="text-sm font-bold text-white/50" data-id="element-1539">Pendientes</p>
          </button>
          <button onClick={() => setActiveFilter(activeFilter === 'preparing' ? 'all' : 'preparing')} className={cn('bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-l-amber-500 shadow-lg text-left cursor-pointer transition-all active:scale-[0.97]', activeFilter === 'preparing' ? 'border-amber-500 ring-2 ring-amber-500/40 bg-amber-500/10' : 'border-white/5 hover:bg-white/[0.03]')} data-id="element-1540">
            <div className="flex items-center justify-between mb-2" data-id="element-1541">
              <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center" data-id="element-1542">
                <ChefHat className="w-4 h-4 text-amber-400" data-id="element-1543" />
              </div>
              <span className="text-2xl font-black tabular-nums" data-id="element-1544">
                {preparingOrders.length}
              </span>
            </div>
            <p className="text-sm font-bold text-white/50" data-id="element-1545">En Cocina</p>
          </button>
          <button onClick={() => setActiveFilter(activeFilter === 'ready' ? 'all' : 'ready')} className={cn('bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-l-emerald-500 shadow-lg text-left cursor-pointer transition-all active:scale-[0.97]', activeFilter === 'ready' ? 'border-emerald-500 ring-2 ring-emerald-500/40 bg-emerald-500/10' : 'border-white/5 hover:bg-white/[0.03]')} data-id="element-1546">
            <div className="flex items-center justify-between mb-2" data-id="element-1547">
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center" data-id="element-1548">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" data-id="element-1549" />
              </div>
              <span className="text-2xl font-black tabular-nums" data-id="element-1550">
                {readyOrders.length}
              </span>
            </div>
            <p className="text-sm font-bold text-white/50" data-id="element-1551">Listos</p>
          </button>
          <button onClick={() => setActiveFilter(activeFilter === 'delayed' ? 'all' : 'delayed')} className={cn('bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-l-red-500 shadow-lg text-left cursor-pointer transition-all active:scale-[0.97]', activeFilter === 'delayed' ? 'border-red-500 ring-2 ring-red-500/40 bg-red-500/10' : 'border-white/5 hover:bg-white/[0.03]')} data-id="element-1552">
            <div className="flex items-center justify-between mb-2" data-id="element-1553">
              <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center" data-id="element-1554">
                <AlertTriangle className="w-4 h-4 text-red-400" data-id="element-1555" />
              </div>
              <span className="text-2xl font-black tabular-nums text-red-400" data-id="element-1556">
                {delayedOrders.length}
              </span>
            </div>
            <p className="text-sm font-bold text-red-400/70" data-id="element-1557">
              Atrasados (&gt;15m)
            </p>
          </button>
        </div>

        {/* Active filter indicator */}
        {activeFilter !== 'all' && <div className="flex items-center gap-2 mb-4" data-id="element-1558">
            <span className="text-sm text-white/50" data-id="element-1559">Filtrando por:</span>
            <span className={cn('px-3 py-1 rounded-full text-xs font-bold', activeFilter === 'new' && 'bg-blue-500/20 text-blue-400', activeFilter === 'preparing' && 'bg-amber-500/20 text-amber-400', activeFilter === 'ready' && 'bg-emerald-500/20 text-emerald-400', activeFilter === 'delayed' && 'bg-red-500/20 text-red-400')} data-id="element-1560">
              {activeFilter === 'new' && `Pendientes (${newOrders.length})`}
              {activeFilter === 'preparing' && `En Cocina (${preparingOrders.length})`}
              {activeFilter === 'ready' && `Listos (${readyOrders.length})`}
              {activeFilter === 'delayed' && `Atrasados (${delayedOrders.length})`}
            </span>
            <button onClick={() => setActiveFilter('all')} className="text-white/30 hover:text-white/60 transition-colors" data-id="element-1561">
              <X className="w-4 h-4" data-id="element-1562" />
            </button>
          </div>}

        {/* Orders Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 items-start" data-id="element-1563">
          {ordersLoading || stationsLoading ? [1, 2, 3, 4].map(i => <div key={i} className="h-64 rounded-2xl bg-[#141414] animate-pulse border border-white/5" data-id="element-1564" />) : (() => {
          const filteredOrders = sortedOrders.filter((order: any) => {
            if (activeFilter === 'all') return true;
            if (activeFilter === 'new') return order.status === 'new' || order.status === 'accepted';
            if (activeFilter === 'preparing') return order.status === 'preparing';
            if (activeFilter === 'ready') return order.status === 'ready' || order.status === 'searching_driver';
            if (activeFilter === 'delayed') {
              const waitMinutes = Math.floor((new Date().getTime() - new Date(order.created_at).getTime()) / 60000);
              return waitMinutes >= 15;
            }
            return true;
          });
          return filteredOrders.length > 0 ? filteredOrders.map((order: any) => {
            const isUpdatingOrder = updatingOrders.has(order.id);
            const waitMinutes = Math.floor((new Date().getTime() - new Date(order.created_at).getTime()) / 60000);
            const stationProgress = new Map<string, {
              total: number;
              ready: number;
              name: string;
            }>();
            order.items.forEach((item: any) => {
              const stationCode = item.station_code || 'sin_asignar';
              const stationName = stations.find(s => s.code === stationCode)?.name || (stationCode === 'sin_asignar' ? 'General' : stationCode);
              if (!stationProgress.has(stationCode)) {
                stationProgress.set(stationCode, {
                  total: 0,
                  ready: 0,
                  name: stationName
                });
              }
              const progress = stationProgress.get(stationCode)!;
              progress.total += item.quantity;
              if (item.status === 'ready' || item.status === 'served') {
                progress.ready += item.quantity;
              }
            });
            const allItemsReady = Array.from(stationProgress.values()).every(p => p.ready === p.total) && order.items.length > 0;
            // Calculate overall progress percentage
            const totalItems = Array.from(stationProgress.values()).reduce((sum, p) => sum + p.total, 0);
            const readyItems = Array.from(stationProgress.values()).reduce((sum, p) => sum + p.ready, 0);
            const overallPercentage = totalItems > 0 ? Math.round(readyItems / totalItems * 100) : 0;
            let borderColor = 'border-white/10';
            if (order.status === 'new' || order.status === 'accepted') borderColor = 'border-blue-500';else if (order.status === 'preparing') borderColor = 'border-amber-500';else if (order.status === 'ready' || order.status === 'searching_driver' || allItemsReady) borderColor = 'border-emerald-500';
            // ── Capricho simplified card ──
            if (isCapricho) {
              const caprichoStatus = order.status === 'new' || order.status === 'accepted' ? 'nuevo' : order.status === 'preparing' ? 'en_preparacion' : order.status === 'ready' || order.status === 'searching_driver' ? 'listo' : 'otro';
              return <div key={order.id} className={cn('bg-[#141414] rounded-2xl flex flex-col border-l-4 border-y border-r border-white/5 overflow-hidden shadow-xl transition-all', caprichoStatus === 'nuevo' ? 'border-blue-500' : caprichoStatus === 'en_preparacion' ? 'border-amber-500' : caprichoStatus === 'listo' ? 'border-emerald-500' : 'border-white/10')} data-id="element-1565">
                          {/* Header */}
                          <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between bg-white/[0.02]" data-id="element-1566">
                            <div className="flex items-center gap-2" data-id="element-1567">
                              <span className="text-lg font-black text-white tabular-nums" data-id="element-1568">
                                #{getShortOrderNumber(order.order_number)}
                              </span>
                              {order.delivery_type === 'delivery' ? <span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1" data-id="element-1569">
                                  <Bike className="w-3 h-3" data-id="element-1570" /> Delivery
                                </span> : <span className="bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1" data-id="element-1571">
                                  <ShoppingBag className="w-3 h-3" data-id="element-1572" /> Llevar
                                </span>}
                            </div>
                            <span className={cn('text-xl font-black tabular-nums', waitMinutes >= 15 ? 'text-red-500' : waitMinutes >= 10 ? 'text-amber-500' : 'text-emerald-500')} data-id="element-1573">
                              {waitMinutes}m
                            </span>
                          </div>

                          {/* Customer */}
                          <div className="px-4 py-3 border-b border-white/5" data-id="element-1574">
                            <p className="text-base font-black text-white" data-id="element-1575">
                              {order.customer_name}
                            </p>
                            {order.customer_phone && <p className="text-xs text-white/40 mt-0.5" data-id="element-1576">
                                {order.customer_phone}
                              </p>}
                            <p className="text-sm font-black text-[#C8A96A] mt-1" data-id="element-1577">
                              ₡{order.total.toLocaleString()}
                            </p>
                          </div>

                          {/* Status */}
                          <div className={cn('px-4 py-2.5 border-b', caprichoStatus === 'nuevo' ? 'bg-blue-500/10 border-blue-500/20' : caprichoStatus === 'en_preparacion' ? 'bg-amber-500/10 border-amber-500/20' : caprichoStatus === 'listo' ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-white/5 border-white/5')} data-id="element-1578">
                            <span className={cn('text-xs font-black uppercase tracking-wider flex items-center gap-1.5', caprichoStatus === 'nuevo' ? 'text-blue-400' : caprichoStatus === 'en_preparacion' ? 'text-amber-400' : caprichoStatus === 'listo' ? 'text-emerald-400' : 'text-white/40')} data-id="element-1579">
                              {caprichoStatus === 'nuevo' && <>
                                  <Clock className="w-3.5 h-3.5" data-id="element-1580" /> Nuevo pedido
                                </>}
                              {caprichoStatus === 'en_preparacion' && <>
                                  <ChefHat className="w-3.5 h-3.5" data-id="element-1581" /> En
                                  preparación
                                </>}
                              {caprichoStatus === 'listo' && <>
                                  <CheckCircle2 className="w-3.5 h-3.5" data-id="element-1582" /> Listo
                                  para entregar
                                </>}
                            </span>
                          </div>

                          {/* Items with toppings */}
                          <div className="p-4 flex-1 bg-[#0B0B0B]/50" data-id="element-1583">
                            <h4 className="text-xs font-black text-white/30 uppercase tracking-wider mb-3" data-id="element-1584">
                              Detalle del pedido
                            </h4>
                            <div className="space-y-2.5" data-id="element-1585">
                              {order.items.map((item: any) => <div key={item.id} data-id="element-1586">
                                  <div className="flex justify-between text-sm" data-id="element-1587">
                                    <span className="text-white font-bold" data-id="element-1588">
                                      {item.quantity}x {item.item_name_snapshot}
                                    </span>
                                    <span className="text-[#C8A96A] font-bold text-xs shrink-0" data-id="element-1589">
                                      ₡
                                      {(item.unit_price * item.quantity).toLocaleString()}
                                    </span>
                                  </div>
                                  {item.options && item.options.length > 0 && <div className="mt-1 ml-4 space-y-0.5" data-id="element-1590">
                                      {item.options.map((opt: any, idx: number) => <div key={idx} className="flex justify-between text-xs" data-id="element-1591">
                                            <span className="text-amber-400/80" data-id="element-1592">
                                              + {opt.option_name}
                                            </span>
                                            {opt.price_delta > 0 && <span className="text-white/30" data-id="element-1593">
                                                +₡
                                                {opt.price_delta.toLocaleString()}
                                              </span>}
                                          </div>)}
                                    </div>}
                                  {item.notes && <p className="text-xs text-white/30 italic mt-1 ml-4" data-id="element-1594">
                                      📝 {item.notes}
                                    </p>}
                                </div>)}
                            </div>
                            {order.notes && <div className="mt-3 pt-3 border-t border-white/5" data-id="element-1595">
                                <p className="text-xs text-white/40 italic" data-id="element-1596">
                                  📝 Nota: {order.notes}
                                </p>
                              </div>}
                          </div>

                          {/* Actions */}
                          <div className="p-3 border-t border-white/5 bg-[#141414]" data-id="element-1597">
                            {caprichoStatus === 'nuevo' && <button onClick={() => handleOrderAction(order.id, 'capricho_receive')} disabled={isUpdatingOrder} className="w-full h-12 rounded-xl text-sm font-black bg-[#8B5E3C] hover:bg-[#A0704A] text-white flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50" data-id="element-1598">
                                {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-1599">
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1600" />
                                    Procesando...
                                  </span> : '✅ PEDIDO RECIBIDO'}
                              </button>}
                            {caprichoStatus === 'en_preparacion' && <button onClick={() => handleOrderAction(order.id, 'capricho_complete')} disabled={isUpdatingOrder} className="w-full h-12 rounded-xl text-sm font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50" data-id="element-1601">
                                {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-1602">
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1603" />
                                    Procesando...
                                  </span> : '🎉 PEDIDO COMPLETADO'}
                              </button>}
                            {caprichoStatus === 'listo' && <button onClick={() => handleOrderAction(order.id, 'deliver')} disabled={isUpdatingOrder} className="w-full h-12 rounded-xl text-sm font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50" data-id="element-1604">
                                {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-1605">
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1606" />
                                    Procesando...
                                  </span> : '🚀 ENTREGAR'}
                              </button>}
                            {['new', 'accepted', 'preparing'].includes(order.status) && <button onClick={() => handleOrderAction(order.id, 'cancel')} disabled={isUpdatingOrder} className="w-full text-center text-[10px] font-bold text-white/20 hover:text-red-500 transition-colors mt-2 uppercase tracking-wider" data-id="element-1607">
                                Cancelar pedido
                              </button>}
                          </div>
                        </div>;
            }
            // ── Standard card (non-Capricho) ──
            return <div key={order.id} className={cn('bg-[#141414] rounded-2xl flex flex-col border-l-4 border-y border-r border-white/5 overflow-hidden shadow-xl transition-all', borderColor, waitMinutes >= 15 && order.status !== 'ready' ? 'shadow-[0_0_15px_rgba(239,68,68,0.15)]' : '')} data-id="element-1608">
                        {/* Order Header */}
                        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between gap-2 bg-white/[0.02]" data-id="element-1609">
                          <div className="flex items-center gap-2 min-w-0" data-id="element-1610">
                            <span className="text-lg font-black text-white tabular-nums" data-id="element-1611">
                              #{getShortOrderNumber(order.order_number)}
                            </span>
                            {order.delivery_type === 'delivery' ? <span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1" data-id="element-1612">
                                <Bike className="w-3 h-3" data-id="element-1613" /> Delivery
                              </span> : order.delivery_type === 'pickup' ? <span className="bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1" data-id="element-1614">
                                <ShoppingBag className="w-3 h-3" data-id="element-1615" /> Llevar
                              </span> : <span className="bg-white/10 text-white/70 px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1" data-id="element-1616">
                                <Store className="w-3 h-3" data-id="element-1617" /> Mesa{' '}
                                {order.table_id || ''}
                              </span>}
                          </div>
                          <span className={cn('text-xl font-black tabular-nums tracking-tighter shrink-0', waitMinutes >= 15 && order.status !== 'ready' ? 'text-red-500 animate-pulse' : waitMinutes >= 10 && order.status !== 'ready' ? 'text-amber-500' : 'text-emerald-500')} data-id="element-1618">
                            {waitMinutes}m
                          </span>
                        </div>

                        {/* Customer & Total */}
                        <div className="px-4 py-2 border-b border-white/5 flex justify-between items-center" data-id="element-1619">
                          <p className="text-sm text-white/70 font-bold truncate pr-2" data-id="element-1620">
                            {order.customer_name}
                          </p>
                          <p className="text-sm font-black text-[#F4D35E] shrink-0" data-id="element-1621">
                            ₡{order.total.toLocaleString()}
                          </p>
                        </div>

                        {/* Status Banner — shows current phase */}
                        {order.status === 'preparing' && <div className="px-4 py-3 bg-amber-500/10 border-b border-amber-500/20" data-id="element-1622">
                            <div className="flex items-center justify-between mb-2" data-id="element-1623">
                              <span className="text-xs font-black text-amber-400 uppercase tracking-wider flex items-center gap-1.5" data-id="element-1624">
                                <ChefHat className="w-3.5 h-3.5" data-id="element-1625" />
                                Enviado a cocina
                              </span>
                              <span className="text-sm font-black text-amber-400 tabular-nums" data-id="element-1626">
                                {overallPercentage}%
                              </span>
                            </div>
                            <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-1627">
                              <div className={cn('h-full rounded-full transition-all duration-700 ease-out', overallPercentage === 100 ? 'bg-emerald-500' : 'bg-amber-500')} style={{
                    width: `${overallPercentage}%`
                  }} data-id="element-1628" />
                            </div>
                          </div>}

                        {(order.status === 'new' || order.status === 'accepted') && <div className="px-4 py-3 bg-blue-500/10 border-b border-blue-500/20" data-id="element-1629">
                            <span className="text-xs font-black text-blue-400 uppercase tracking-wider flex items-center gap-1.5" data-id="element-1630">
                              <Clock className="w-3.5 h-3.5" data-id="element-1631" />
                              Nuevo pedido — Pendiente de enviar a cocina
                            </span>
                          </div>}

                        {order.status === 'ready' && <div className="px-4 py-3 bg-emerald-500/10 border-b border-emerald-500/20" data-id="element-1632">
                            <span className="text-xs font-black text-emerald-400 uppercase tracking-wider flex items-center gap-1.5" data-id="element-1633">
                              <CheckCircle2 className="w-3.5 h-3.5" data-id="element-1634" />
                              ¡Listo para entregar!
                            </span>
                          </div>}

                        {/* Station Progress — only show when preparing */}
                        {(order.status === 'preparing' || order.status === 'ready') && <div className="p-4 flex-1 bg-[#0B0B0B]/50" data-id="element-1635">
                            <h4 className="text-xs font-black text-white/30 uppercase tracking-wider mb-3" data-id="element-1636">
                              Progreso por estación
                            </h4>
                            {stationProgress.size === 0 ? <p className="text-sm text-white/40 italic" data-id="element-1637">
                                Sin items
                              </p> : <div className="space-y-3" data-id="element-1638">
                                {Array.from(stationProgress.entries()).map(([code, progress]) => {
                    const isComplete = progress.ready === progress.total;
                    const percentage = Math.round(progress.ready / progress.total * 100);
                    return <div key={code} className="space-y-1.5" data-id="element-1639">
                                        <div className="flex justify-between items-center text-sm" data-id="element-1640">
                                          <span className={cn('font-bold truncate pr-2', isComplete ? 'text-emerald-400' : 'text-white/80')} data-id="element-1641">
                                            {isComplete ? '✅' : '🔥'}{' '}
                                            {progress.name}
                                          </span>
                                          <span className={cn('font-black tabular-nums shrink-0', isComplete ? 'text-emerald-400' : 'text-amber-400')} data-id="element-1642">
                                            {progress.ready} / {progress.total}
                                          </span>
                                        </div>
                                        <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-1643">
                                          <div className={cn('h-full rounded-full transition-all duration-500', isComplete ? 'bg-emerald-500' : 'bg-amber-500')} style={{
                          width: `${percentage}%`
                        }} data-id="element-1644" />
                                        </div>
                                      </div>;
                  })}
                              </div>}
                          </div>}

                        {/* For new/accepted orders, show items list */}
                        {(order.status === 'new' || order.status === 'accepted') && <div className="p-4 flex-1 bg-[#0B0B0B]/50" data-id="element-1645">
                            <h4 className="text-xs font-black text-white/30 uppercase tracking-wider mb-3" data-id="element-1646">
                              Items del pedido
                            </h4>
                            <div className="space-y-1.5" data-id="element-1647">
                              {order.items.map((item: any) => <div key={item.id} className="flex justify-between text-sm" data-id="element-1648">
                                  <span className="text-white/70 truncate pr-2" data-id="element-1649">
                                    {item.quantity}x {item.item_name_snapshot}
                                  </span>
                                  <span className="text-white/40 text-xs shrink-0" data-id="element-1650">
                                    {stations.find(s => s.code === item.station_code)?.name || 'General'}
                                  </span>
                                </div>)}
                            </div>
                          </div>}

                        {/* Action Footer */}
                        <div className="p-3 border-t border-white/5 bg-[#141414]" data-id="element-1651">
                          {(order.status === 'new' || order.status === 'accepted') && <button onClick={() => handleOrderAction(order.id, order.status === 'new' ? 'send_to_kitchen' : 'start_preparation')} disabled={isUpdatingOrder} className="w-full h-12 rounded-xl text-sm font-black bg-amber-500 hover:bg-amber-400 text-black flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50" data-id="element-1652">
                              {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-1653">
                                  <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" data-id="element-1654" />
                                  Enviando a cocina...
                                </span> : '🔥 ENVIAR A COCINA'}
                            </button>}
                          {order.status === 'preparing' && <button onClick={() => handleOrderAction(order.id, 'mark_ready')} disabled={isUpdatingOrder || !allItemsReady} className={cn('w-full h-12 rounded-xl text-sm font-black flex items-center justify-center gap-2 transition-all', allItemsReady ? 'bg-emerald-500 hover:bg-emerald-400 text-black active:scale-95 shadow-[0_0_15px_rgba(16,185,129,0.2)]' : 'bg-white/5 text-white/30 cursor-not-allowed')} data-id="element-1655">
                              {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-1656">
                                  <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" data-id="element-1657" />
                                  Procesando...
                                </span> : allItemsReady ? '✅ MARCAR LISTO' : <span className="flex items-center gap-2" data-id="element-1658">
                                  <span className="w-4 h-4 border-2 border-white/20 border-t-amber-400 rounded-full animate-spin" data-id="element-1659" />
                                  EN COCINA — {overallPercentage}% LISTO
                                </span>}
                            </button>}
                          {order.status === 'ready' && <button onClick={() => handleOrderAction(order.id, 'deliver')} disabled={isUpdatingOrder} className="w-full h-12 rounded-xl text-sm font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50" data-id="element-1660">
                              {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-1661">
                                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1662" />
                                  Procesando...
                                </span> : '🚀 ENTREGAR AL CLIENTE'}
                            </button>}
                          {['new', 'accepted', 'preparing'].includes(order.status) && <button onClick={() => handleOrderAction(order.id, 'cancel')} disabled={isUpdatingOrder} className="w-full text-center text-[10px] font-bold text-white/20 hover:text-red-500 transition-colors mt-2 uppercase tracking-wider" data-id="element-1663">
                              Cancelar pedido
                            </button>}
                        </div>
                      </div>;
          }) : <div className="col-span-full py-24 flex flex-col items-center justify-center text-white/30 bg-[#141414] rounded-2xl border border-white/5 border-dashed" data-id="element-1664">
                    <CheckCircle2 className="w-16 h-16 text-white/10 mb-4" data-id="element-1665" />
                    <h3 className="text-xl font-black text-white/40 mb-1" data-id="element-1666">
                      {activeFilter !== 'all' ? 'Sin pedidos en esta categoría' : 'Todo bajo control'}
                    </h3>
                    <p className="text-sm" data-id="element-1667">
                      {activeFilter !== 'all' ? 'No hay pedidos que coincidan con este filtro.' : 'No hay pedidos activos en este momento.'}
                    </p>
                    {activeFilter !== 'all' && <button onClick={() => setActiveFilter('all')} className="mt-4 px-4 py-2 rounded-lg bg-white/10 text-white/60 text-sm font-bold hover:bg-white/15 transition-colors" data-id="element-1668">
                        Ver todos los pedidos
                      </button>}
                  </div>;
        })()}
        </div>

        {/* ═══ TOAST NOTIFICATIONS ═══ */}
        {toasts.length > 0 && <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 pointer-events-none" data-id="element-1669">
            {toasts.map(toast => <div key={toast.id} className={cn('pointer-events-auto flex items-center gap-3 px-5 py-3 rounded-xl shadow-2xl border text-sm font-bold min-w-[280px] max-w-[400px] anim-slide-up', toast.type === 'success' ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-red-600 border-red-500 text-white')} data-id="element-1670">
                <span className="flex-1" data-id="element-1671">{toast.message}</span>
                <button onClick={() => removeToast(toast.id)} className="p-0.5 hover:bg-white/20 rounded transition-colors shrink-0" data-id="element-1672">
                  <X className="w-4 h-4" data-id="element-1673" />
                </button>
              </div>)}
          </div>}
      </div>
    </AdminLayout>;
}